import React, { useState, useRef, useEffect } from 'react';
import { tasksApi, teamsApi, labelsApi } from '../services/api';
import { toast } from 'sonner';
import { X, Trash2, ChevronDown, Check, CheckCircle2, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DatePicker from './task/pickers/DatePicker';
import ProjectPicker from './ui/ProjectPicker';
import { useDateTime } from '../contexts/DateTimeContext';
import { statusName } from '../lib/statusName';

// Single-select dropdown (Status, Priority, Project)
const Dropdown = ({ label, options, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/60 border border-border rounded-lg text-foreground/80 text-sm hover:border-input hover:text-foreground transition-colors disabled:opacity-50 whitespace-nowrap"
      >
        {label}
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 min-w-[180px] max-h-60 overflow-y-auto bg-accent border border-border rounded-xl shadow-2xl py-1 z-50">
          {options.map(opt => (
            <button
              key={opt.value}
              onClick={() => { onSelect(opt.value); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/60 transition-colors"
            >
              {opt.dot && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: opt.dot }} />}
              <span className={opt.color || 'text-foreground/80'}>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Multi-select assignee dropdown - stays open, checkmarks, Apply button
const AssigneeDropdown = ({ members, selectedTasks, onAssign, disabled, t }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const ref = useRef(null);

  // When dropdown opens, initialise selection from existing task assignees
  useEffect(() => {
    if (!open) return;
    const assignedLists = selectedTasks.map(t => t.assigned_to || []);
    // Pre-check users assigned to ALL selected tasks
    const fullyAssigned = members
      .filter(m => assignedLists.length > 0 && assignedLists.every(list => list.includes(m.user_id)))
      .map(m => m.user_id);
    setSelected(fullyAssigned);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // For each member: fully assigned (all tasks), partial (some tasks), none
  const assignedLists = selectedTasks.map(t => t.assigned_to || []);
  const getState = (uid) => {
    const inAll = assignedLists.length > 0 && assignedLists.every(list => list.includes(uid));
    const inSome = assignedLists.some(list => list.includes(uid));
    if (selected.includes(uid)) return 'selected';   // user toggled on
    if (inAll) return 'all';                          // already on all tasks
    if (inSome) return 'partial';                     // on some tasks
    return 'none';
  };

  const toggle = (uid) => {
    setSelected(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const handleApply = () => {
    onAssign(selected);
    setOpen(false);
    setSelected([]);
  };

  const handleUnassign = () => {
    onAssign([]);
    setOpen(false);
    setSelected([]);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/60 border border-border rounded-lg text-foreground/80 text-sm hover:border-input hover:text-foreground transition-colors disabled:opacity-50 whitespace-nowrap"
      >
        {t('tasks.assignee')}
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 min-w-[210px] bg-accent border border-border rounded-xl shadow-2xl py-1 z-50">
          {/* Unassign all */}
          <button
            onClick={handleUnassign}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent/60 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            {t('bulk_action_bar.unassign_all')}
          </button>

          {members.length > 0 && <div className="border-t border-border my-1" />}

          {/* Members list with checkboxes */}
          <div className="max-h-48 overflow-y-auto">
            {members.map(m => {
              const state = getState(m.user_id);
              const isChecked = state === 'selected' || state === 'all';
              const isPartial = state === 'partial';
              return (
                <button
                  key={m.user_id}
                  onClick={() => toggle(m.user_id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/60 transition-colors"
                >
                  {/* Checkbox */}
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isChecked ? 'bg-amber-500 border-amber-500'
                    : isPartial ? 'bg-accent border-input'
                    : 'border-input'
                  }`}>
                    {isChecked && <Check className="w-2.5 h-2.5 text-black" />}
                    {isPartial && <span className="w-2 h-0.5 bg-white rounded-full block" />}
                  </div>
                  {/* Avatar */}
                  <div className="w-5 h-5 rounded-full bg-indigo-400 dark:bg-indigo-600 flex items-center justify-center text-[10px] font-medium text-white shrink-0">
                    {(m.full_name || m.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <span className="text-foreground/80 truncate">{m.full_name || m.email || m.user_id}</span>
                </button>
              );
            })}
          </div>

          {/* Apply button */}
          {selected.length > 0 && (
            <div className="border-t border-border mt-1 px-2 pt-1.5 pb-1">
              <button
                onClick={handleApply}
                className="w-full py-1.5 rounded-lg bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400 transition-colors"
              >
                {t('bulk_action_bar.assign_members', { count: selected.length })}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Labels dropdown: checked = on all selected tasks, dash = on some. Toggling
// sets the desired end state; Apply translates that into add/remove lists.
const LabelsDropdown = ({ labels, selectedTasks, onApply, disabled, t }) => {
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState({}); // labelId -> true (add) / false (remove)
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTouched({});
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const labelLists = selectedTasks.map(t => t.label_ids || []);
  const inAll = (id) => labelLists.length > 0 && labelLists.every(list => list.includes(id));
  const inSome = (id) => labelLists.some(list => list.includes(id));

  const handleApply = () => {
    const add = Object.keys(touched).filter(id => touched[id]);
    const remove = Object.keys(touched).filter(id => !touched[id]);
    if (add.length || remove.length) onApply(add, remove);
    setOpen(false);
  };

  const hasChanges = Object.keys(touched).length > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/60 border border-border rounded-lg text-foreground/80 text-sm hover:border-input hover:text-foreground transition-colors disabled:opacity-50 whitespace-nowrap"
      >
        {t('tasks.labels')}
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 min-w-[210px] bg-accent border border-border rounded-xl shadow-2xl py-1 z-50">
          <div className="max-h-48 overflow-y-auto">
            {labels.map(l => {
              const isChecked = touched[l.id] ?? inAll(l.id);
              const isPartial = !isChecked && touched[l.id] === undefined && inSome(l.id);
              return (
                <button
                  key={l.id}
                  onClick={() => setTouched(prev => ({ ...prev, [l.id]: !isChecked }))}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/60 transition-colors"
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                    isChecked ? 'bg-amber-500 border-amber-500'
                    : isPartial ? 'bg-accent border-input'
                    : 'border-input'
                  }`}>
                    {isChecked && <Check className="w-2.5 h-2.5 text-black" />}
                    {isPartial && <span className="w-2 h-0.5 bg-white rounded-full block" />}
                  </div>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: l.color || '#6b7280' }} />
                  <span className="text-foreground/80 truncate">{l.name}</span>
                </button>
              );
            })}
          </div>
          {hasChanges && (
            <div className="border-t border-border mt-1 px-2 pt-1.5 pb-1">
              <button
                onClick={handleApply}
                className="w-full py-1.5 rounded-lg bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400 transition-colors"
              >
                {t('common.apply')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BulkActionBar = ({ selectedIds, selectedTasks = [], meta, onClear, onUpdate, onDelete }) => {
  const { t } = useTranslation();
  const { toLocalDateString } = useDateTime();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [labels, setLabels] = useState([]);
  const { customStatuses = [] } = meta;
  const ids = [...selectedIds];
  const count = ids.length;

  useEffect(() => {
    teamsApi.getAll().then(res => {
      const map = {};
      (res.data || []).forEach(team => {
        (team.members || []).forEach(m => { map[m.user_id] = m; });
      });
      setMembers(Object.values(map));
    }).catch(() => {});
    labelsApi.getAll().then(res => setLabels(res.data || [])).catch(() => {});
  }, []);

  const handleBulk = async (fields) => {
    setLoading(true);
    try {
      await tasksApi.bulkUpdate({ task_ids: ids, ...fields });
      toast.success(t('bulk_action_bar.updated', { count }));
      onUpdate();
    } catch {
      toast.error(t('bulk_action_bar.update_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (field, value) => handleBulk({ [field]: value });

  const handleDuplicate = async () => {
    setLoading(true);
    try {
      await Promise.all(ids.map(id => tasksApi.duplicate(id)));
      toast.success(t('bulk_action_bar.duplicated', { count }));
      onUpdate();
    } catch {
      toast.error(t('bulk_action_bar.update_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await tasksApi.bulkDelete({ task_ids: ids });
      toast(t('bulk_action_bar.moved_to_trash', { count }));
      onDelete();
    } catch {
      toast.error(t('bulk_action_bar.delete_failed'));
      setLoading(false);
    }
  };

  const statusOptions = customStatuses
    .filter(s => s.category !== 'archived')
    .map(s => ({ value: s.id, label: statusName(s, t), dot: s.color }));

  const PRIORITIES = [
    { value: 'p1', label: t('bulk_action_bar.priority_urgent'), color: 'text-red-400' },
    { value: 'p2', label: t('bulk_action_bar.priority_high'),   color: 'text-amber-700 dark:text-amber-400' },
    { value: 'p3', label: t('bulk_action_bar.priority_medium'), color: 'text-blue-400' },
    { value: 'p4', label: t('bulk_action_bar.priority_low'),    color: 'text-muted-foreground' },
  ];

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 bg-popover border border-border rounded-2xl shadow-2xl"
      style={{ minWidth: 'max-content' }}
    >
      <span className="text-sm font-medium text-foreground whitespace-nowrap">
        {t('bulk_action_bar.selected', { count })}
      </span>

      <div className="w-px h-5 bg-accent" />

      {statusOptions.length > 0 && (
        <Dropdown
          label={t('common.status')}
          options={statusOptions}
          onSelect={(val) => handleUpdate('status_id', val)}
          disabled={loading}
        />
      )}

      <Dropdown
        label={t('common.priority')}
        options={PRIORITIES}
        onSelect={(val) => handleUpdate('priority', val)}
        disabled={loading}
      />

      {/* App-standard date picker (quick dates + calendar, honours the user's
          date settings via DateTimeContext) */}
      <DatePicker onChange={(date) => handleUpdate('due_date', date ? toLocalDateString(date) : null)}>
        <button
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/60 border border-border rounded-lg text-foreground/80 text-sm hover:border-input hover:text-foreground transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          {t('tasks.due_date')}
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </DatePicker>

      {/* App-standard searchable project picker (grouped by team, quick-create) */}
      <ProjectPicker
        selectedProjectId={null}
        onSelect={(pid) => handleUpdate('project_id', pid || null)}
        trigger={
          <button
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/60 border border-border rounded-lg text-foreground/80 text-sm hover:border-input hover:text-foreground transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {t('common.project')}
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        }
      />

      {members.length > 0 && (
        <AssigneeDropdown
          members={members}
          selectedTasks={selectedTasks}
          onAssign={(userIds) => handleUpdate('assigned_to', userIds)}
          disabled={loading}
          t={t}
        />
      )}

      {labels.length > 0 && (
        <LabelsDropdown
          labels={labels}
          selectedTasks={selectedTasks}
          onApply={(add, remove) => handleBulk({ add_label_ids: add, remove_label_ids: remove })}
          disabled={loading}
          t={t}
        />
      )}

      <div className="w-px h-5 bg-accent" />

      <button
        disabled={loading}
        onClick={() => handleUpdate('completed', true)}
        title={t('bulk_action_bar.mark_complete')}
        className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-colors disabled:opacity-50"
      >
        <CheckCircle2 className="w-4 h-4" />
      </button>

      <button
        disabled={loading}
        onClick={handleDuplicate}
        title={t('common.duplicate')}
        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
      >
        <Copy className="w-4 h-4" />
      </button>

      <button
        disabled={loading}
        onClick={handleDelete}
        title={t('tasks.move_to_trash')}
        className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
      >
        <Trash2 className="w-4 h-4" />
      </button>

      <button
        disabled={loading}
        onClick={onClear}
        title={t('bulk_action_bar.clear_selection')}
        className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default BulkActionBar;
